import { useState, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import { fetchRFZOUpcomingArticles } from "../../../store/rfzoTherapy-actions";
import { useGlobalContext } from "../../../store/context/GlobalContext";
import { useUnifiedFilters } from "../../../hooks/useUnifiedFilters";
import {
  ReminderView,
  UnifiedReminderState,
  ReminderFilters,
  PatientReminderData,
} from "../../../types/reminderTypes";
import {
  transformPatientsData,
  transformToArticleView,
  transformAggregatedData,
} from "../utils/dataTransforms";

export const useReminderData = () => {
  const dispatch = useDispatch();
  const { fetchWrap } = useGlobalContext();
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  const isAdmin = useSelector((state: RootState) => state.auth.isAdmin);
  const marketplaceInfo = useSelector(
    (state: RootState) => state.auth.marketplaceInfo
  );
  const rfzoUpcomingArticles = useSelector(
    (state: RootState) => state.rfzoTherapy.rfzoUpcomingArticles
  );
  const loadingRFZOUpcomingArticles = useSelector(
    (state: RootState) => state.rfzoTherapy.loadingRFZOUpcomingArticles
  );

  // Single state management, no competing refs
  const [state, setState] = useState<UnifiedReminderState>({
    selectedView: ReminderView.PATIENTS,
    // ✅ FIX: Remove filters from local state - read directly from URL
    data: {
      patients: [],
      articles: [],
      aggregated: [],
    },
    loading: {
      patients: false,
      articles: false,
      aggregated: false,
    },
    error: {
      patients: null,
      articles: null,
      aggregated: null,
    },
  });

  // ✅ FIX: Read filters directly from URL via useUnifiedFilters
  const { filters } = useUnifiedFilters(state.selectedView);

  // Refresh counter for manual refresh triggers
  const [refreshCounter, setRefreshCounter] = useState(0);

  // Single parameter tracking for deduplication
  const lastFetchParams = useRef<string>("");

  const setSelectedView = useCallback((view: ReminderView) => {
    console.log("🔵 setSelectedView called:", view);
    setState((prev) => ({ ...prev, selectedView: view }));
  }, []);

  // ✅ FIX: Remove setFilters function - no longer needed

  // Refresh trigger function
  const triggerRefresh = useCallback(() => {
    console.log("🔵 triggerRefresh called");
    setRefreshCounter((prev) => prev + 1);
  }, []);

  // Immediate data update method for optimistic updates
  const updatePatientsData = useCallback(
    (updatedPatients: PatientReminderData[]) => {
      console.log(
        "🟢 updatePatientsData called with:",
        updatedPatients.length,
        "patients"
      );
      setState((prev) => ({
        ...prev,
        data: {
          ...prev.data,
          patients: updatedPatients,
          // Also update articles view since it depends on patients data
          articles: transformToArticleView(updatedPatients),
        },
      }));
    },
    []
  );

  // Use GlobalContext with direct filter passing
  const fetchWithFilters = useCallback(
    async (filters: ReminderFilters, statuses: string = "") => {
      console.log("🟢 fetchWithFilters called with:", {
        filters,
        statuses,
      });

      const remindersFilter = {
        createdFrom: filters.dateRange.startDate,
        createdTo: filters.dateRange.endDate,
        groups: filters.groups,
        statuses: statuses,
        therapyTaken: filters.therapyTaken,
        sort: filters.sort,
      };

      // Pass filters directly to fetchWrap - no race condition!
      return fetchWrap(statuses, isAdmin, remindersFilter);
    },
    [fetchWrap, isAdmin]
  );

  // PERFORMANCE: Cleanup effect to prevent memory leaks
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Cancel any in-flight requests on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const fetchPatientsData = useCallback(
    async (filters: ReminderFilters) => {
      console.log("🟢 fetchPatientsData called with filters:", filters);

      // Cancel any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();
      const controller = abortControllerRef.current;

      setState((prev) => ({
        ...prev,
        loading: { ...prev.loading, patients: true },
        error: { ...prev.error, patients: null },
      }));

      try {
        // Use GlobalContext with direct filter passing - maintains DRY!
        const response = await fetchWithFilters(
          filters,
          filters.statuses || ""
        );

        // Only update state if component is still mounted and request wasn't aborted
        if (!isMountedRef.current || controller.signal.aborted) {
          return;
        }

        let rawData = response?.data?.elements || [];
        console.log("🟢 Raw patients data received:", rawData.length, "items");

        // Transform data first to ensure we have proper structure
        const transformedData = transformPatientsData(rawData);
        console.log(
          "🟢 Transformed patients data:",
          transformedData.length,
          "items"
        );

        if (isMountedRef.current && !controller.signal.aborted) {
          setState((prev) => ({
            ...prev,
            data: { ...prev.data, patients: transformedData },
            loading: { ...prev.loading, patients: false },
          }));
        }
      } catch (error) {
        if (isMountedRef.current && !controller.signal.aborted) {
          console.error("🔴 Failed to fetch patients data:", error);
          setState((prev) => ({
            ...prev,
            loading: { ...prev.loading, patients: false },
            error: { ...prev.error, patients: "Failed to fetch patients data" },
          }));
        }
      }
    },
    [fetchWithFilters]
  );

  const fetchAggregatedData = useCallback(
    async (filters: ReminderFilters) => {
      console.log("🟡 fetchAggregatedData called with filters:", filters);

      setState((prev) => ({
        ...prev,
        loading: { ...prev.loading, aggregated: true },
        error: { ...prev.error, aggregated: null },
      }));

      try {
        const isAdminUser =
          isAdmin || localStorage.getItem("roles")?.includes("ADMIN");
        console.log("🟡 Is admin user:", isAdminUser);
        console.log("🟡 Current marketplace info:", marketplaceInfo);

        if (isAdminUser) {
          console.log(
            "🟢 Dispatching fetchRFZOUpcomingArticles as admin with dates:",
            filters.dateRange.startDate,
            "to",
            filters.dateRange.endDate
          );
          const result = await dispatch<any>(
            fetchRFZOUpcomingArticles(
              filters.dateRange.startDate,
              filters.dateRange.endDate
            )
          );
          console.log("🟢 Admin dispatch result:", result);
        } else {
          const branchMarketplaceId =
            Number(marketplaceInfo?.branchMarketplaceId) || 0;
          const marketplaceId = Number(marketplaceInfo?.marketplaceId) || 0;

          console.log("🟡 Marketplace info processed:", {
            branchMarketplaceId,
            marketplaceId,
            original: marketplaceInfo,
          });

          if (branchMarketplaceId === 0 || marketplaceId === 0) {
            throw new Error("Marketplace IDs are required for non-admin users");
          }

          console.log(
            "🟢 Dispatching fetchRFZOUpcomingArticles as regular user with:",
            {
              dateFrom: filters.dateRange.startDate,
              dateTo: filters.dateRange.endDate,
              branchMarketplaceId,
              marketplaceId,
            }
          );

          const result = await dispatch<any>(
            fetchRFZOUpcomingArticles(
              filters.dateRange.startDate,
              filters.dateRange.endDate,
              branchMarketplaceId,
              marketplaceId
            )
          );
          console.log("🟢 Regular user dispatch result:", result);
        }

        console.log("🟢 Aggregated data fetch completed successfully");

        setState((prev) => ({
          ...prev,
          loading: { ...prev.loading, aggregated: false },
        }));
      } catch (error) {
        console.error("🔴 Failed to fetch aggregated data:", error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to fetch aggregated data";
        setState((prev) => ({
          ...prev,
          loading: { ...prev.loading, aggregated: false },
          error: { ...prev.error, aggregated: errorMessage },
        }));
      }
    },
    [dispatch, isAdmin, marketplaceInfo]
  );

  // Single fetch effect with collision-safe parameter key generation
  useEffect(() => {
    // Collision-safe parameter key generation using JSON.stringify
    const currentParams = JSON.stringify({
      view: state.selectedView,
      dateStart: filters.dateRange.startDate,
      dateEnd: filters.dateRange.endDate,
      groups: filters.groups || "",
      statuses: filters.statuses || "",
      therapyTaken: filters.therapyTaken || "",
      sort: filters.sort || [],
      refresh: refreshCounter,
    });

    // Only fetch if params changed AND we have required data
    if (
      lastFetchParams.current !== currentParams &&
      filters.dateRange.startDate &&
      filters.dateRange.endDate &&
      (isAdmin || marketplaceInfo) // Ensure marketplace ready for non-admin
    ) {
      lastFetchParams.current = currentParams;

      console.log("🔵 Fetch triggered with params:", currentParams);

      // Simple fetch logic using filters directly
      if (
        state.selectedView === ReminderView.PATIENTS ||
        state.selectedView === ReminderView.ARTICLES
      ) {
        fetchPatientsData(filters);
      } else if (state.selectedView === ReminderView.AGGREGATED) {
        fetchAggregatedData(filters);
      }
    }
  }, [
    state.selectedView,
    filters, // ✅ FIX: Use filters directly instead of state.filters
    refreshCounter,
    isAdmin,
    marketplaceInfo,
    fetchPatientsData,
    fetchAggregatedData,
  ]);

  // Update aggregated data when Redux state changes
  useEffect(() => {
    console.log(
      "🔵 Redux state update - rfzoUpcomingArticles:",
      rfzoUpcomingArticles?.length || 0,
      "items, loading:",
      loadingRFZOUpcomingArticles
    );

    if (rfzoUpcomingArticles) {
      let transformedData = transformAggregatedData(rfzoUpcomingArticles);
      console.log(
        "🔵 Transformed aggregated data:",
        transformedData.length,
        "items"
      );

      setState((prev) => ({
        ...prev,
        data: { ...prev.data, aggregated: transformedData },
      }));
    }
  }, [rfzoUpcomingArticles, loadingRFZOUpcomingArticles]);

  // Generate articles view from patients data
  useEffect(() => {
    const currentPatientsData = state.data.patients;
    console.log(
      "🔵 Patients data updated, current length:",
      currentPatientsData.length
    );

    // Always update articles view, even if empty (to clear previous data)
    let articlesData = transformToArticleView(currentPatientsData);
    console.log("🔵 Generated articles data:", articlesData.length, "articles");

    setState((prev) => ({
      ...prev,
      data: { ...prev.data, articles: articlesData },
    }));
  }, [state.data.patients]);

  return {
    ...state,
    filters, // ✅ FIX: Expose filters from useUnifiedFilters
    setSelectedView,
    // ✅ FIX: Remove setFilters from return
    triggerRefresh, // Expose refresh function
    updatePatientsData, // Expose the immediate update method
    // Helper functions
    getCurrentLoading: () => {
      switch (state.selectedView) {
        case ReminderView.PATIENTS:
          return state.loading.patients;
        case ReminderView.ARTICLES:
          return state.loading.articles || state.loading.patients; // Articles depend on patients
        case ReminderView.AGGREGATED:
          return state.loading.aggregated || loadingRFZOUpcomingArticles; // Use Redux loading state
        default:
          return false;
      }
    },
    getCurrentError: () => {
      switch (state.selectedView) {
        case ReminderView.PATIENTS:
          return state.error.patients;
        case ReminderView.ARTICLES:
          return state.error.articles || state.error.patients; // Articles depend on patients
        case ReminderView.AGGREGATED:
          return state.error.aggregated;
        default:
          return null;
      }
    },
  };
};
